import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import ModalService from "src/modals"
import { findLobby, setLobbies } from "src/store/lobby"
import { selectGameModes, selectMapSizes } from "src/store/common"
import { gameModeName } from "src/utils/gameModes"
import { playUiSound } from "src/utils/audio"
import { mapSizeOptionLabel } from "src/utils/mapSizes"

import Modal from "src/components/modals/Modal"
import { apiErrorLines } from "src/utils/apiErrors"

import "./FindLobbyModal.css"

// How long the modal waits between two searches while no room is open. Short, because a room the
// queue assembles out of several searchers (`lobby/matchmaking.py`) reaches the others on their
// next ask, and the server forgets a searcher who has not asked for 20 seconds.
export const SEARCH_EVERY_MS = 5000
// How long one line of the waiting banter stays up before another replaces it.
export const LINE_EVERY_MS = 3500

// Any line but the one showing, so the banter never seems stuck.
const nextLine = (count, current) => {
  if (count < 2) return 0
  const pick = Math.floor(Math.random() * (count - 1))
  return pick >= current ? pick + 1 : pick
}

/* "No room is open right now" is not something the player did wrong, so it is not an error:
   it puts the modal in a searching state that asks again every SEARCH_EVERY_MS until a room
   turns up or the player cancels. It used to print the server's English sentence in red. The
   server names the refusal (`code`), which is how this tells it from a real one. The panel says
   nothing about how the search works (owner, 2026-09-28): a line of banter, picked at random
   from `lobbyModals.searchingLines` and changed every LINE_EVERY_MS. */
const FindLobbyModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // The server's own list — see CreateLobbyModal for why it is not typed out here.
  const gameModes = useSelector(selectGameModes)
  // The map sizes the server offers (requirement 35, M12); "" searches every one.
  const mapSizes = useSelector(selectMapSizes)

  const [errors, setErrors] = useState([])
  const [searching, setSearching] = useState(false)
  const lines = t("lobbyModals.searchingLines", { returnObjects: true })
  const lineCount = Array.isArray(lines) ? lines.length : 0
  const [line, setLine] = useState(0)
  const [formData, setFormData] = useState({
    mode: "",
    team: "",
    // "" is either (owner, 2026-10-04: ranked or not is a setting of the search too).
    ranked: "",
    mapSize: "",
  })
  const timer = useRef(null)
  // Which search is the live one. Cancelling or closing bumps it, so an answer already on the
  // wire cannot start the clock again. A room found is still honoured: the server has seated
  // the player in it by then, and the lobby view is where they are.
  const searchId = useRef(0)
  // Whether the server holds this player in its search queue, so leaving tells it.
  const queued = useRef(false)

  // Out of the server's queue too, or the player could be seated in a room assembled from the
  // searchers for the next 20 seconds. A room assembled before the press is theirs already — they
  // are seated in it — so it is entered rather than left behind unseen.
  const leaveQueue = () => {
    if (!queued.current) return
    queued.current = false
    apiClient
      .post("/lobby/find/stop/", {}, { handlesOwnErrors: true })
      .then((response) => {
        if (response?.data?.number) {
          dispatch(setLobbies([response.data]))
          playUiSound("found")
          ModalService.hideAll()
        }
      })
      .catch(() => {})
  }

  const stopSearching = () => {
    searchId.current += 1
    clearTimeout(timer.current)
    setSearching(false)
    leaveQueue()
  }

  useEffect(
    () => () => {
      searchId.current += 1
      clearTimeout(timer.current)
      leaveQueue()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (!searching) return undefined
    setLine((current) => nextLine(lineCount, current))
    const banter = setInterval(() => setLine((current) => nextLine(lineCount, current)), LINE_EVERY_MS)
    return () => clearInterval(banter)
  }, [searching, lineCount])

  const search = (criteria, id) => {
    dispatch(findLobby(criteria))
      .unwrap()
      .then(() => {
        queued.current = false
        clearTimeout(timer.current)
        playUiSound("found")
        ModalService.hideAll()
      })
      .catch((refusal) => {
        if (id !== searchId.current) return
        if (refusal?.code === "NoAvailableLobby") {
          queued.current = true
          setSearching(true)
          timer.current = setTimeout(() => search(criteria, id), SEARCH_EVERY_MS)
          return
        }
        stopSearching()
        if (refusal?.code === "UserAlreadyInLobbyError") setErrors(t("lobbyModals.findAlreadyInLobby"))
        else setErrors(refusal ?? t("errors.somethingWentWrong", { defaultValue: "Something went wrong." }))
      })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrors([])
    searchId.current += 1
    search(
      {
        mode: formData.mode || null,
        team: formData.team || null,
        ranked: formData.ranked === "" ? null : formData.ranked === "ranked",
        ...(mapSizes.length ? { map_size: formData.mapSize } : {}),
      },
      searchId.current
    )
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  return (
    <Modal>
      <h1>{t("lobbyModals.findTitle")}</h1>
      {apiErrorLines(errors).map(({ key, text }) => (
        <p key={key} className="errored-text">
          <br />
          {text}
        </p>
      ))}
      {searching && (
        <div className="find-lobby-searching" role="status">
          <span className="find-lobby-radar" aria-hidden="true" />
          <p key={line} className="find-lobby-searching-line">
            {lineCount ? lines[line] : ""}
          </p>
        </div>
      )}
      <br />
      <br />
      <form onSubmit={handleSubmit}>
        <label htmlFor="mode">{t("lobbyModals.modeLabel")}</label>
        <select id="mode" name="mode" className="form-select" value={formData.mode} onChange={handleInputChange} disabled={searching}>
          <option value="">{t("lobbyModals.any")}</option>
          {gameModes.map((mode) => (
            <option key={mode} value={mode}>
              {gameModeName(mode)}
            </option>
          ))}
        </select>

        <label htmlFor="team">{t("lobbyModals.teamLabel")}</label>
        <select id="team" name="team" className="form-select" value={formData.team} onChange={handleInputChange} disabled={searching}>
          <option value="">{t("lobbyModals.any")}</option>
          <option value="human">{t("lobbyModals.teamHumans")}</option>
          <option value="zombie">{t("lobbyModals.teamZombies")}</option>
        </select>

        <label htmlFor="ranked">{t("lobbyModals.rankedLabel")}</label>
        <select id="ranked" name="ranked" className="form-select" value={formData.ranked} onChange={handleInputChange} disabled={searching}>
          <option value="">{t("lobbyModals.any")}</option>
          <option value="ranked">{t("lobbyModals.ranked")}</option>
          <option value="unranked">{t("lobbyModals.unranked")}</option>
        </select>

        {mapSizes.length > 0 && (
          <>
            <label htmlFor="mapSize">{t("lobby.mapSize.label")}</label>
            <select id="mapSize" name="mapSize" className="form-select" value={formData.mapSize} onChange={handleInputChange} disabled={searching}>
              <option value="">{t("lobby.mapSize.any")}</option>
              {mapSizes.map((row) => (
                <option key={row.size} value={row.size}>
                  {mapSizeOptionLabel(row)}
                </option>
              ))}
            </select>
          </>
        )}

        <br />
        <br />
        {/* Two keys, so two elements. With one, React turned the clicked STOP into a submit
            button during a real click — it re-renders before the browser runs the click's
            default action — and the browser then submitted the form: the search started again
            on the press that was meant to end it. A script's `.click()` does not show it. */}
        {searching ? (
          <button key="stop" type="button" className="quiet" onClick={stopSearching}>
            {t("lobbyModals.stopSearching")}
          </button>
        ) : (
          <button key="search" type="submit">
            {t("lobbyModals.findButton")}
          </button>
        )}
      </form>
    </Modal>
  )
}

export default FindLobbyModal
